import { useCallback, useEffect, useState } from 'react'
import {
  isRegisteredMember, memberPhase,
  type ApplicationRecord, type MemberPhase, type ProfileRecord
} from '@romain130492/topchopsticks'
import { api } from '../lib/api'
import { clearAllStorage, clearToken, getRefreshToken, getToken, setRefreshToken, setToken } from '../lib/storage'

/**
 * WHO IS THIS. One hook, one answer, app-wide.
 *
 * The rule is the mini program's, deliberately:
 *
 *     a JWT in storage == member.  No JWT == guest.  That is the whole test.
 *
 * And NOTHING signs anyone in implicitly. On the mini program a silent
 * wx.login on every mount created a session (and a profile row) for anyone who
 * merely opened the app, which made "logged in" meaningless and made signing
 * out impossible — the next launch signed you straight back in. The web has no
 * silent equivalent, and must not grow one: a session begins only when someone
 * enters an SMS code.
 */

interface SessionState {
  loading: boolean
  profile: ProfileRecord | null
  application: ApplicationRecord | null
}

let state: SessionState = { loading: Boolean(getToken()), profile: null, application: null }
const listeners = new Set<(next: SessionState) => void>()

const publish = (next: Partial<SessionState>) => {
  state = { ...state, ...next }
  listeners.forEach((listener) => listener(state))
}

/**
 * Is the token itself worthless, or did the request merely fail?
 *
 * ONLY 401 AND 404 MEAN "SIGN OUT".
 *   401 — the JWT expired or was signed with a different secret.
 *   404 — the JWT is valid but points at a profile that no longer exists
 *         (deleted account, wiped test database).
 *
 * Everything else — a 500, a 502, or `fetch` rejecting outright because the
 * backend is not listening — says nothing about the token. Treating those as
 * a dead session is why restarting the local backend logged you out and made
 * you re-enter your phone number: the page refreshed mid-restart, `/me` could
 * not connect, and a perfectly good token was deleted.
 *
 * The mini program has always drawn this line correctly
 * (`const stale = error?.status === 401 || error?.status === 404` in
 * services/auth.js). The web did not.
 */
const isDeadToken = (error: unknown): boolean => {
  const status = (error as { status?: number })?.status
  return status === 401 || status === 404
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Resume an existing session. NEVER creates one. */
export async function resumeSession(): Promise<void> {
  if (!getToken()) {
    publish({ loading: false, profile: null, application: null })
    return
  }
  publish({ loading: true })

  // A restarting backend is unreachable for a second or two. Retrying briefly
  // turns that into a spinner instead of a logout — and a dead token stops
  // immediately, so nobody waits on an outcome that cannot change.
  const delays = [0, 500, 1500]
  let lastError: unknown = null

  for (const delay of delays) {
    if (delay) await sleep(delay)
    try {
      const profile = await api.getMe()
      const application = await api.getMyApplication().catch(() => null)
      publish({ loading: false, profile, application })
      return
    } catch (error) {
      lastError = error
      if (isDeadToken(error)) {
        // Genuinely signed out. http.ts has already dropped the token; do it
        // here too so this function is correct on its own.
        console.warn('[tc][session] token rejected, continuing as guest', error)
        clearToken()
        publish({ loading: false, profile: null, application: null })
        return
      }
    }
  }

  // Still failing, but the token is NOT known to be bad — so keep it. The next
  // refresh or navigation retries, and the member is still signed in when the
  // backend comes back.
  console.error('[tc][session] could not reach the backend; KEEPING the token', lastError)
  publish({ loading: false })
}

export async function signInWithSms(phone: string, code: string, country = '86') {
  const result = await api.verifySmsCode(phone, code, country)
  setToken(result.token)
  // Both halves, together. The access token is short-lived now; without this
  // the session would end at its first expiry instead of being renewed.
  setRefreshToken(result.refresh_token)
  const application = await api.getMyApplication().catch(() => null)
  publish({ loading: false, profile: result.profile, application })
  return result.profile
}

export function signOut() {
  // Revoke SERVER-side first, so the refresh token cannot be replayed from a
  // copy of this browser's storage. Fire-and-forget: a failed revoke must not
  // keep someone signed in on the screen in front of them.
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    api.logout(refreshToken).catch((error) => {
      console.warn('[tc][session] server-side revoke failed; token stays valid until it expires', error)
    })
  }
  clearToken()
  publish({ loading: false, profile: null, application: null })
}

/** Dev escape hatch: wipe everything, not just the token. */
export function forceSignOut() {
  clearAllStorage()
  publish({ loading: false, profile: null, application: null })
}

export function setApplicationLocally(application: ApplicationRecord | null) {
  publish({ application })
}
export function setProfileLocally(profile: ProfileRecord | null) {
  publish({ profile })
}

export interface Session {
  loading: boolean
  profile: ProfileRecord | null
  application: ApplicationRecord | null
  hasSession: boolean
  registered: boolean
  phase: MemberPhase
  refresh: () => Promise<void>
}

export function useSession(): Session {
  const [local, setLocal] = useState<SessionState>(state)

  useEffect(() => {
    listeners.add(setLocal)
    return () => { listeners.delete(setLocal) }
  }, [])

  const refresh = useCallback(() => resumeSession(), [])
  const hasSession = Boolean(getToken())

  return {
    ...local,
    hasSession,
    registered: hasSession && isRegisteredMember(local.profile, local.application),
    phase: memberPhase(hasSession, local.profile, local.application),
    refresh
  }
}
